import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Home } from 'lucide-react';
import useAffiliateConfig from '../hooks/useAffiliateConfig';
import { safeFetch } from '../utils/api';

import { getBackendUrl, getApiUrl } from '../config/environment';
const BACKEND_URL = getBackendUrl();

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, signup } = useAuth();
  const { commissionRate, commissionPerSale, loading: configLoading } = useAffiliateConfig();
  const [isAffiliateLogin, setIsAffiliateLogin] = useState(false);
  
  // 🔒 P2 FIX: Initialize referralCode and isSignup synchronously from URL params
  // This avoids calling setState in useEffect which triggers cascading renders
  const [referralCode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('ref') || '';
  });
  
  // Phase B MOA: Support licence entreprise depuis email d'invitation
  const [enterpriseData] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      isEnterprise: params.get('enterprise') === 'true',
      licenseId: params.get('license_id') || '',
      prefilledEmail: params.get('email') || ''
    };
  });
  
  // 🔒 P2 FIX: Initialize isSignup based on referralCode presence OR enterprise invite (synchronous)
  const [isSignup, setIsSignup] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return !!params.get('ref') || params.get('enterprise') === 'true';
  });
  
  const [formData, setFormData] = useState(() => ({
    email: new URLSearchParams(window.location.search).get('email') || '',
    password: '',
    name: '',
    affiliateCode: ''
  }));
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  
  const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  
  const validateEmail = (email) => {
    const trimmed = email.trim();
    if (!trimmed) return '';
    if (!EMAIL_REGEX.test(trimmed)) return 'Adresse email invalide (ex: nom@domaine.com)';
    return '';
  };
  
  // 🔒 P2 FIX: Track if toast has been shown (ref to avoid re-renders)
  const toastShownRef = useRef(false);

  // 🔒 P2 FIX: Show welcome toast only once (side effect, not state change)
  useEffect(() => {
    if (referralCode && !toastShownRef.current) {
      toastShownRef.current = true;
      toast.info(`🎉 Vous êtes invité par un affilié ! Code: ${referralCode}`);
    }
    // Phase B MOA: Toast pour invitation entreprise
    if (enterpriseData.isEnterprise && !toastShownRef.current) {
      toastShownRef.current = true;
      toast.success(`🎁 Vous avez reçu un accès Premium entreprise !`);
    }
  }, [referralCode, enterpriseData.isEnterprise]);

  const handleAffiliateLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await safeFetch(`${BACKEND_URL}/api/affiliate/login-with-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ affiliate_code: formData.affiliateCode })
      });

      if (response.ok) {
        const data = await response.json();
        // Store token and user data
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        toast.success('Connexion réussie en tant qu\'affilié !');
        window.location.href = '/affiliate-dashboard';
      } else {
        const error = await response.json();
        setLoading(false);
        toast.error(error.detail || 'Code affilié invalide');
      }
    } catch (error) {
      setLoading(false);
      toast.error('Erreur de connexion');
      console.error('Affiliate login error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isAffiliateLogin) {
      return handleAffiliateLogin(e);
    }
    
    // Niveau 1: Validation frontend
    const error = validateEmail(formData.email);
    if (error) {
      setEmailError(error);
      return;
    }
    
    setLoading(true);

    try {
      // Phase B MOA: Passer le licenseId si inscription entreprise
      const result = isSignup 
        ? await signup(formData.email, formData.password, formData.name, referralCode, enterpriseData.licenseId || null)
        : await login(formData.email, formData.password);

      if (result.success) {
        if (result.needsVerification) {
          // Signup: redirect to email verification
          navigate(`/verify-email?email=${encodeURIComponent(formData.email.trim().toLowerCase())}`);
          return;
        }
        
        // Phase B MOA: Message adapté pour activation entreprise
        if (result.enterpriseActivated) {
          toast.success('Compte créé et accès Premium entreprise activé !');
        } else {
          toast.success('Connexion réussie!');
        }

        // 🔗 Deep-link return : si l'utilisateur arrive avec ?next=/route-interne,
        //    on respecte sa destination d'origine (ex. lien email
        //    « Consulter le rapport » → /pulse/batches/{id}). Sécurité : on
        //    n'accepte QUE les chemins relatifs internes (commencent par "/"
        //    et pas "//" ni "/\\"), pour empêcher tout open-redirect.
        const nextParam = new URLSearchParams(window.location.search).get('next');
        const safeNext =
          nextParam
          && nextParam.startsWith('/')
          && !nextParam.startsWith('//')
          && !nextParam.startsWith('/\\')
            ? nextParam
            : null;
        window.location.href = safeNext || '/training';
      } else {
        setLoading(false);
        if (result.emailNotVerified) {
          navigate(`/verify-email?email=${encodeURIComponent(formData.email.trim().toLowerCase())}`);
          return;
        }
        toast.error(result.error);
      }
    } catch (error) {
      setLoading(false);
      toast.error('Une erreur est survenue');
      console.error('Login error:', error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" 
         style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #dbeafe 100%)' }}>
      <div className="w-full max-w-md">
        {/* Back to Home Button */}
        <div className="mb-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-blue-700 hover:text-blue-900 transition-colors font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour à l&apos;accueil</span>
          </button>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#0c4a6e' }}>
            Coach DIA
          </h1>
          <p className="text-lg" style={{ color: '#075985' }}>Votre coach IA pour managers</p>
        </div>

        <Card data-testid="auth-card" className="border-2 shadow-xl">
          <CardHeader>
            <CardTitle data-testid="auth-title">
              {isAffiliateLogin 
                ? '🎯 Connexion Affilié' 
                : (isSignup ? 'Créer un compte' : 'Se connecter')}
            </CardTitle>
            <CardDescription>
              {isAffiliateLogin
                ? 'Accédez à votre dashboard affilié avec votre code'
                : (isSignup 
                  ? 'Rejoignez Coach DIA pour booster votre leadership'
                  : 'Connectez-vous pour accéder à vos modules')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {isAffiliateLogin ? (
                // Affiliate Code Login Form
                <div className="space-y-2">
                  <Label htmlFor="affiliateCode">Code Affilié</Label>
                  <Input
                    id="affiliateCode"
                    data-testid="affiliate-code-input"
                    type="text"
                    placeholder="Ex: ABC12345"
                    value={formData.affiliateCode}
                    onChange={(e) => setFormData({ ...formData, affiliateCode: e.target.value.toUpperCase() })}
                    required
                    className="font-mono text-lg"
                  />
                  <p className="text-xs text-gray-500">
                    💡 Entrez votre code affilié pour accéder à votre dashboard
                  </p>
                </div>
              ) : (
                // Standard Login/Signup Form
                <>
                  {referralCode && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-green-800 font-medium mb-1">
                        🎉 Inscription via parrainage
                      </p>
                      <p className="text-xs text-green-600">
                        Code affilié : <span className="font-mono font-bold">{referralCode}</span>
                      </p>
                    </div>
                  )}
                  
                  {/* Phase B MOA: Bandeau invitation entreprise */}
                  {enterpriseData.isEnterprise && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-purple-800 font-medium mb-1">
                        🎁 Accès Premium Entreprise
                      </p>
                      <p className="text-xs text-purple-600">
                        Créez votre compte pour activer votre accès offert par votre entreprise.
                      </p>
                    </div>
                  )}
                  
                  {isSignup && (
                    <div className="space-y-2">
                      <Label htmlFor="name">Nom complet</Label>
                      <Input
                        id="name"
                        data-testid="name-input"
                        type="text"
                        placeholder="Jean Dupont"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required={isSignup}
                      />
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      data-testid="email-input"
                      type="email"
                      placeholder="vous@exemple.com"
                      value={formData.email}
                      onChange={(e) => {
                        setFormData({ ...formData, email: e.target.value });
                        if (emailError) setEmailError(validateEmail(e.target.value));
                      }}
                      onBlur={(e) => setEmailError(validateEmail(e.target.value))}
                      required
                      disabled={!!enterpriseData.prefilledEmail}
                      className={`${enterpriseData.prefilledEmail ? 'bg-gray-100 cursor-not-allowed' : ''} ${emailError ? 'border-red-400 focus:ring-red-400' : ''}`}
                    />
                    {emailError && (
                      <p className="text-xs text-red-500" data-testid="email-error">{emailError}</p>
                    )}
                    {enterpriseData.prefilledEmail && (
                      <p className="text-xs text-gray-500">
                        Email associé à votre licence entreprise
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">Mot de passe</Label>
                      {!isSignup && (
                        <button
                          type="button"
                          onClick={() => navigate('/forgot-password')}
                          className="text-xs text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                        >
                          Mot de passe oublié ?
                        </button>
                      )}
                    </div>
                    <Input
                      id="password"
                      data-testid="password-input"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                    />
                  </div>
                </>
              )}

              <Button 
                data-testid="submit-button"
                type="submit" 
                className="w-full" 
                disabled={loading}
                style={{ background: isAffiliateLogin ? '#d97706' : '#0284c7' }}
              >
                {loading ? 'Chargement...' : (
                  isAffiliateLogin 
                    ? '🚀 Accéder au Dashboard' 
                    : (isSignup ? 'Créer mon compte' : 'Se connecter')
                )}
              </Button>

              <div className="text-center text-sm space-y-2">
                {!isAffiliateLogin && (
                  <button
                    data-testid="toggle-auth-mode"
                    type="button"
                    onClick={() => setIsSignup(!isSignup)}
                    className="block w-full text-blue-600 hover:underline"
                  >
                    {isSignup 
                      ? 'Déjà un compte ? Se connecter' 
                      : 'Pas encore de compte ? S\'inscrire'}
                  </button>
                )}
                
                <button
                  data-testid="toggle-affiliate-mode"
                  type="button"
                  onClick={() => {
                    setIsAffiliateLogin(!isAffiliateLogin);
                    setIsSignup(false);
                  }}
                  className="block w-full text-amber-600 hover:underline font-medium"
                >
                  {isAffiliateLogin 
                    ? '← Retour à la connexion classique' 
                    : '🎯 Je suis affilié - Me connecter avec mon code'}
                </button>
              </div>
            </form>

            {/* Affiliate Program CTA */}
            {!isAffiliateLogin && (
              <div className="mt-6 pt-6 border-t">
                <div className="text-center mb-3">
                  <p className="text-sm text-gray-600 mb-2">
                    {configLoading ? (
                      <span className="animate-pulse">💰 Chargement...</span>
                    ) : (
                      `💰 Gagnez jusqu'à ${commissionRate}% de commission`
                    )}
                  </p>
                </div>
                <a
                  href="/affiliation"
                  className="block w-full px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all text-center no-underline"
                  style={{ 
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'rgba(255, 255, 255, 0.3)',
                  }}
                >
                  🚀 Rejoindre le Programme Affiliation
                </a>
                <p className="text-xs text-gray-500 text-center mt-2">
                  {configLoading ? (
                    <span className="animate-pulse">Chargement...</span>
                  ) : (
                    `Partagez Coach DIA et gagnez ${commissionPerSale.toFixed(2)}€ par filleul`
                  )}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;